import React, { useEffect } from "react";
import { FiInbox, FiX } from "react-icons/fi";
import type { Tone } from "./StatCard";

/* ---------- Badge ---------- */
interface BadgeProps {
  tone?: Tone;
  dot?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const Badge = ({ tone = "neutral", dot = false, children, className = "" }: BadgeProps) => (
  <span
    className={`zs-badge ${tone !== "neutral" ? `zs-badge-${tone}` : ""} ${dot ? "zs-badge-dot" : ""} ${className}`}
  >
    {children}
  </span>
);

/* ---------- Empty state ---------- */
interface EmptyStateProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export const EmptyState = ({ title, description, icon, action, className = "" }: EmptyStateProps) => (
  <div className={`zs-empty ${className}`}>
    <span className="zs-empty-icon">{icon ?? <FiInbox />}</span>
    <p className="zs-empty-title">{title}</p>
    {description && <p className="zs-empty-text">{description}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

/* ---------- Loading ---------- */
export const Spinner = ({ className = "" }: { className?: string }) => (
  <span className={`zs-spinner inline-block ${className}`} role="status" aria-label="Loading" />
);

export const LoadingBlock = ({ label, className = "" }: { label?: React.ReactNode; className?: string }) => (
  <div className={`flex flex-col items-center justify-center gap-3 py-12 text-sm text-ink-3 ${className}`}>
    <Spinner className="w-6 h-6" />
    {label && <span>{label}</span>}
  </div>
);

export const SkeletonRows = ({ rows = 5, className = "" }: { rows?: number; className?: string }) => (
  <div className={`flex flex-col gap-3 p-4 ${className}`} aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="h-9 zs-skeleton" />
    ))}
  </div>
);

/* ---------- Modal chrome (presentation only; callers own open/close state) ---------- */
interface ModalFrameProps {
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
  labelledBy?: string;
  /** Called when the backdrop itself is clicked. Defaults to no-op. */
  onBackdropClick?: () => void;
  /** Opt-in: call `onClose` on Escape (for dialogs migrated from antd Modal). */
  closeOnEscape?: boolean;
}

export const ModalFrame = ({
  children,
  onClose,
  className = "",
  labelledBy,
  onBackdropClick,
  closeOnEscape = false,
}: ModalFrameProps) => {
  useEffect(() => {
    if (!closeOnEscape || !onClose) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [closeOnEscape, onClose]);

  return (
  <div
    className="zs-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby={labelledBy}
    onClick={(event) => {
      if (event.target === event.currentTarget) onBackdropClick?.();
    }}
  >
    <div className={`zs-modal ${className}`}>
      {onClose && (
        <button type="button" className="zs-icon-btn zs-modal-close" onClick={onClose} aria-label="Close">
          <FiX />
        </button>
      )}
      {children}
    </div>
  </div>
  );
};
